import { CSSProperties, MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  atom,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { Align } from 'react-window';
import ReactBaseTable from 'react-base-table';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { useQuery } from '@apollo/client';
import { Markup } from 'interweave';

import * as K from 'common/constants';
import {
  FETCH_ORIGIN,
  LogToFetch,
  Settings,
  TRANSFORM_TYPE,
  TransformArgs,
} from 'common/types';
import { LogEntry, LogEntryEdge, QueryType, SortOrder } from 'common/graphqlTypes';
import { PlaceholderLogEntryEdge, useEventCache } from 'hooks/useLRQ/useEventCache';
import { getRequestIds } from 'common/util/lrqRequest';
import { convertTimestampToMilliseconds } from 'common/util/time';
import { transformContent } from 'common/util/transform';

import { searchParamState, selectedTokenState } from 'components/common/Managers/RouteStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { anchorCursorState, downwardThresholdState, upwardThresholdState } from 'components/search/SearchLogsPage/SearchStateManager';
import {
  eventSortOrderState,
  isScrollReadyState,
  logResultsScrollExtremes,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import {
  anchorLogRequestsState,
  logResultsStatus,
  paginatedLogRequestsState,
  useProgressiveStateManagement,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import { SeeMoreState, seeMoreState } from 'components/common/Dialog/SeeMoreDialog';
import { GET_LOG_ENTRY } from 'components/search/queries';

/**
 * This state acts as a message for the windowing library to scroll to a particular cursor (continuationToken)
 */
export type LogResultsScrollOffset = {
  align?: Align;
  cursor?: string; // continuationToken of the LogEntryEdge
  index?: number;
};

export const logResultsScrollOffset = atom<LogResultsScrollOffset>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_SCROLL_OFFSET,
  default: {},
});

/**
 * Scroll state tracking: timeMarker atom state.
 *  Letting us know what's the currently centered visible log row timestamp
 */
export const logResultsScrollTimeMarker = atom({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_SCROLL_TIME_MARKER,
  default: 0,
});

/**
 * Tracks the index and "inverted" index of the current visibly centered log. The invertIndex is the index that
 *  the same event would occupy if the sort order were reversed. For example, if there are 100 logs and the
 *  center index is 5 (0,1,2,3,4 are above), then the invertIndex is 94 (95,96,97,98,99 are below).
 */
export const visibleCenterIndexState = atom<{ index?: number, invertIndex?: number }>({
  key: K.RECOIL_ATOM_KEY.VISIBLE_CENTER_INDEX,
  default: {},
});

/**
 * Which log to fetch via 'getLogEntry', and why, in order to show full data
 */
export const logToFetchState = atom<LogToFetch | null>({
  key: K.RECOIL_ATOM_KEY.LOG_TO_FETCH,
  default: null,
});

/**
 * Log to show in the LogDetails inspector after fetching it
 */
export const logToInspectState = atom<LogEntry | null>({
  key: K.RECOIL_ATOM_KEY.LOG_TO_INSPECT,
  default: null,
});

/**
 * Data to be passed down to the individual items rendered by the windowing components
 */
export type LogResultsItemData = {
  filter: string;
  logEntryEdges: Array<LogEntryEdge | PlaceholderLogEntryEdge>;
  selectedToken: string;
  settings: Settings;
  timezone: string | null;
};

export type SearchLogLineProps = {
  data:  LogResultsItemData;
  index: number;
  style: CSSProperties;
};

export interface ListOnItemsRenderedProps {
  overscanStartIndex?: number;
  overscanStopIndex?: number;
  visibleStartIndex: number;
  visibleStopIndex: number;
}

export type LogResultsState = {
  itemData: LogResultsItemData;
  onItemsRendered: (props: ListOnItemsRenderedProps) => void;
  scrollOffset: LogResultsScrollOffset;
};

// Live Tail

export const isLiveTailActiveState = atom<boolean>({
  key: K.RECOIL_ATOM_KEY.IS_LIVE_TAIL_ACTIVE,
  default: false,
});

// #region Util functions

/**
 * identify if the current LogLine is active
 */
export function checkIsActive(props: SearchLogLineProps): boolean {
  const {
    index,
    data: {
      logEntryEdges,
      selectedToken,
    },
  } = props;

  const continuationToken = logEntryEdges[index]?.continuationToken || '';
  return !!selectedToken && selectedToken === continuationToken;
}

/**
 * Identify if two LogLines are equal, for memoization purposes
 */
export function areEqual(prevProps: SearchLogLineProps, nextProps: SearchLogLineProps): boolean {
  // Verify if isActive has changed
  const isNextActive = checkIsActive(nextProps);
  const isPrevActive = checkIsActive(prevProps);
  const isSameActiveStatus = isNextActive === isPrevActive;

  // Verify if unique identifiers (i.e. continuation tokens) do not match
  // For example, mew identifiers are set when a new search is issued
  const prevContinuationToken = prevProps.data.logEntryEdges[prevProps.index]?.continuationToken;
  const nextContinuationToken = nextProps.data.logEntryEdges[nextProps.index]?.continuationToken;
  const isSameContinuationToken = prevContinuationToken === nextContinuationToken;

  // Verify if data that can affect LogLine representation has changed
  const isSameSettings = prevProps.data.settings === nextProps.data.settings;
  const isSameTimezone = prevProps.data.timezone === nextProps.data.timezone;
  const isSameFilter = prevProps.data.filter === nextProps.data.filter;
  const isSameStyles = JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style);

  return isSameActiveStatus
    && isSameContinuationToken
    && isSameStyles
    && isSameSettings
    && isSameTimezone
    && isSameFilter;
}

// #endregion

/**
 *  Custom hook that returns the state needed to display log lines in any display mode.
 */
export function useLogResultsState(): LogResultsState {

  const filter = useRecoilValue(searchParamState(K.URL_PARAM.FILTER));
  const scrollOffset = useRecoilValue(logResultsScrollOffset);
  const selectedToken = useRecoilValue(selectedTokenState);
  const settings = useRecoilValue(settingsState);
  const timezone = useRecoilValue(timezoneState);

  // Getting anchor ids
  const anchorLogRequests = useRecoilValue(anchorLogRequestsState);
  const anchorIds = getRequestIds(anchorLogRequests, QueryType.LOG);

  // Getting Ids of current logEntries
  const logEntryRequests = useRecoilValue(paginatedLogRequestsState);
  const eventCacheIds = getRequestIds(logEntryRequests, QueryType.LOG);
  let eventCache = useEventCache(eventCacheIds, true);
  const allDone = eventCache.results?.length && eventCache.results?.every((result) => result.isDone);
  const isLoading = !allDone && eventCache.paginatedData?.currentIds.length === 0;

  eventCache = useProgressiveStateManagement(eventCache, eventCacheIds, logResultsStatus, isLoading);
  const { paginatedData: { currentLogEntryEdges: logEntryEdges } } = eventCache;

  /**
   *  Create a memoized and throttled callback to be supplied to the onItemsRendered prop of the virtual lists from the
   *  react-window library.
   *
   *  This callback might perform any of three recoil state updates in response to these values changing:
   *
   *  1. Update the extremes to indicate that a user has scrolled to the top or bottom of the list (used to disable the
   *  start and end buttons)
   *  2. Update the timestamp of the currently centered log line for the indicator on the timeline
   *  3. Update the state for ascending or descending thresholds to determine when new continuation requests should be
   *  made
   *
   *  Caution: this callback is optimized for performance. These operations were previously separated and were found to
   *  be a performance bottleneck; any future changes should be wary of performance regression.
   */
  const onItemsRenderedRecoilCallback = useRecoilCallback(({ set, snapshot }) => ({ visibleStartIndex, visibleStopIndex }: ListOnItemsRenderedProps) => {

    // Find currently visible centered logEntry and track its index state
    const visibleCenterIndex = visibleStartIndex + Math.floor((visibleStopIndex - visibleStartIndex) / 2);
    set(
      visibleCenterIndexState,
      {
        index: visibleCenterIndex,
        invertIndex: (logEntryEdges.length - 1) - visibleCenterIndex,
      },
    );

    // Getting timestamp of currently visible centered logEntry
    const logEntry = logEntryEdges[visibleCenterIndex]?.logEntry || null;
    const timestamp = logEntry ? convertTimestampToMilliseconds((logEntry as LogEntry).timestamp) : null;
    if (timestamp) {
      set(logResultsScrollTimeMarker, timestamp);
    }

    // calculate new extremes
    const isAtTheTop = visibleStartIndex <= 2;
    const isAtTheBottom = visibleStopIndex >= logEntryEdges.length - 3;
    const extremes = {
      isAtTheTop,
      isAtTheBottom,
    };

    // update the extremes if they have changed
    set(logResultsScrollExtremes, (currVal) => {
      if (isEqual(currVal, extremes)) {
        return currVal;
      }
      return extremes;
    });

    // Update thresholds when the current visibleStopIndex (ascending) or visibleStartIndex (descending) crosses a new
    // threshold. We do this by applying an immer-style mutation to the thresholds array that includes every index that
    // would come before the threshold index in that particular scrolling direction as well. This is to account for the
    // possibility of indexes being skipped by very fast scrolling, especially on Firefox.
    const anchorCursor = snapshot.getLoadable(anchorCursorState).contents;
    const anchorCursorIndex = logEntryEdges.findIndex(({ continuationToken }) => continuationToken === anchorCursor);
    // additional manually tracked state to represent that the list is in some kind of transitional state, such as when
    // switching view modes.
    const isScrollReady = snapshot.getLoadable(isScrollReadyState).contents;

    const sortOrder = snapshot.getLoadable(eventSortOrderState).getValue();

    // Calculating thresholds causes unpredictable results while the scroll component is transitioning between states.
    // If there is no anchor cursor, no index for the anchor cursor, or if the isScrollReadyState is false it indicates
    // that the list is not ready yet, and thus this section should be a no-op.
    if (isScrollReady && anchorCursor && anchorCursorIndex !== -1) {
      // Find the index of the ascending anchor request in the list of active log entry requests in the current pagination model
      const ascendingAnchorId = anchorIds.find((id) => id?.includes('"ascending":true'));

      // Find the index of the descending anchor request in the list of active log entry requests in the current pagination model
      const descendingAnchorId = anchorIds.find((id) => id?.includes('"ascending":false'));
      const downwardAnchorId = sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM ? ascendingAnchorId : descendingAnchorId;
      const upwardAnchorId = sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM ? descendingAnchorId : ascendingAnchorId;

      const downwardAnchorRequestIndex = downwardAnchorId ? eventCacheIds.indexOf(downwardAnchorId) : -1;
      const upwardAnchorRequestIndex = upwardAnchorId ? eventCacheIds.indexOf(upwardAnchorId) : -1;

      // calculate the downward offset, which is the number of indexes between the visibleStopIndex and the anchor cursor, with a buffer of half a block
      const downwardThresholdOffset = (visibleStopIndex - (K.REQUEST_BLOCK_SIZE / 2)) - anchorCursorIndex;

      // calculate the upward offset, which is the number of indexes between the visibleStartIndex and the anchor cursor, with a buffer of half a block
      const upwardThresholdOffset = anchorCursorIndex - (visibleStartIndex + (K.REQUEST_BLOCK_SIZE / 2));

      // We only track downward thresholds when there is a downward anchor request. We can also skip this update if the result of the offset calculation was negative
      if (downwardAnchorRequestIndex > -1 && downwardThresholdOffset > -1) {
        const crossedThresholdIndex = Math.floor(downwardThresholdOffset / K.REQUEST_BLOCK_SIZE) + downwardAnchorRequestIndex;
        set(downwardThresholdState, (downwardThresholds: Array<boolean | undefined>) => produce(downwardThresholds, (draft: Array<boolean>) => {
          // Set each downward threshold index from the anchor to the crossedThresholdIndex calculated from the scroll position change
          for (let index = downwardAnchorRequestIndex; index <= crossedThresholdIndex; index += 1) {
            draft[index] = true;
          }
        }));
      }

      // We only track upward thresholds when there is an upward anchor request. We can also skip this update if the result of the offset calculation was negative
      if (upwardAnchorRequestIndex > -1 && upwardThresholdOffset > -1) {
        const crossedThresholdIndex = upwardAnchorRequestIndex - Math.floor(upwardThresholdOffset / K.REQUEST_BLOCK_SIZE);
        set(upwardThresholdState, (upwardThresholds: Array<boolean | undefined>) => produce(upwardThresholds, (draft: Array<boolean>) => {
          // Set each upward threshold index from the anchor to the crossedThresholdIndex calculated from the scroll position change
          for (let index = upwardAnchorRequestIndex; index >= crossedThresholdIndex; index -= 1) {
            draft[index] = true;
          }
        }));
      }
    }
  }, [ logEntryEdges ]);
  // Throttle and then memoize so that onItemsRendered is a stable reference
  const onItemsRendered = useCallback(
    throttle(onItemsRenderedRecoilCallback, 500, { 'leading': false }),
    [ logEntryEdges ],
  );

  // itemData is memoized for performance reasons only
  const itemData = useMemo(() => ({
    filter,
    logEntryEdges,
    selectedToken,
    settings,
    timezone,
  }), [
    filter,
    logEntryEdges,
    selectedToken,
    settings,
    timezone,
  ]);

  return {
    itemData,
    onItemsRendered,
    scrollOffset,
  };
}

/**
 * Functionality to position scroll on `scrollOffset.cursor` change.
 * Also handles the case where rows have dynamic height which is lazily calculated on scroll
 */
export function useScrollPositioningHandler<T>(scrollOffset: LogResultsScrollOffset, logIds: string[]): MutableRefObject<ReactBaseTable<T> | null> {
  const setIsScrollReady = useSetRecoilState(isScrollReadyState);
  const baseTableRef = useRef<ReactBaseTable<T> | null>(null);
  const scrollIndex = useRef<null | number>(null); // to have refence to the value in prev. render
  const rowsHeight = useRef<null | number>(null); // to have refence to the value in prev. render
  const scrollMatchCounter = useRef<number>(0);

  const cleanup = (interval?: NodeJS.Timeout) => {
    if (interval) {
      scrollMatchCounter.current = 0;
      clearInterval(interval);
    }
  };

  /**
   * Effect is triggered when the `cursor` is changed and handles two scenarios:
   *  (1): positioning to +1/-1 item in the list (`arrow keys` use case)
   *  (2): positioning to an arbitrary item in the list as per `cursor` provided
   */
  useEffect(() => {
    /**
     * Case (1): positioning to +1/-1 item in the list (`arrow keys` use case)
     * In this scenario we avoid using `setInterval` as it may introduce a visual lag betwen `arrow key` pressed and new row line is highlighted
     */
    const prevScrollIndex = scrollIndex.current;
    const newScrollIndex = scrollOffset?.index !== undefined ? scrollOffset.index : logIds.indexOf(scrollOffset?.cursor || ''); // TODO-perf: `indexOf` is an O(n) operation, we should optimize to O(1) e.g. via having `scrollOffset.index`
    scrollIndex.current = newScrollIndex;

    if (prevScrollIndex !== null && Math.abs(newScrollIndex - prevScrollIndex) === 1) {
      baseTableRef.current?.scrollToRow(newScrollIndex);
      return (): void => cleanup();
    }

    /**
     * Case (2): positioning to an arbitrary item in the list as per `cursor` provided
     * Due to rows having dynamic height it's not enough to just position scroll once on mount – we should 'scroll to cursor' -> 'let correct row sizes to be rendered' -> 'scroll to cursor' (repeat till positioned correctly)
     */
    const interval = setInterval(() => { // this logic will be run continuously until the scroll position is correct
      const newRowsHeight = baseTableRef.current?.getTotalRowsHeight() || null;
      if (newRowsHeight === rowsHeight.current) { // we ensure that scroll is positioned correctly via checking that rows are not being resized anymore
        scrollMatchCounter.current += 1;
        if (scrollMatchCounter.current === 3) {
          // after three matches, call clearInterval and set isScrollReady to true so that the onItemsRendered callback
          // can resume threshold calculations.
          clearInterval(interval);
          setIsScrollReady(true);
          return;
        }
      }

      rowsHeight.current = newRowsHeight;

      baseTableRef.current?.scrollToRow(newScrollIndex, 'smart'); // `smart` - on index change within viewport use case it does minimal necessary scroll; on new positioning it does centering
    }, 0);

    return (): void => cleanup(interval);
  },
  // Eslint suggests including `scrollOffset` and `logIds` - this is unnecessary here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [scrollOffset]);

  return baseTableRef;
}

/**
 * This hook handles generating and processing a 'getLogEntry' request when the
 * user has clicked in order to get the full data of a log.
 */
export const useGetFullLogData = () => {

  const settings = useRecoilValue(settingsState);
  const setSelectedToken = useSetRecoilState(selectedTokenState);
  const [ logToFetch, setLogToFetch ] = useRecoilState(logToFetchState);
  const setLogToInspect = useSetRecoilState(logToInspectState);
  const setSeeMore = useSetRecoilState(seeMoreState);

  const { data: logLineData } = useQuery(GET_LOG_ENTRY, {
    skip: !logToFetch,
    variables: { continuationToken: logToFetch?.continuationToken },
    // for some reason this request messes up caching and makes readFragment() in useEventCache fail
    fetchPolicy: 'no-cache',
  });

  // Linkify and pretty-print values we show in the "See More" dialog
  const transformValue = (facet: string, text: string): string => {
    const transforms: TRANSFORM_TYPE[] = [];
    const transformArgs: TransformArgs = {};
    transforms.push(TRANSFORM_TYPE.LINKIFY);
    transformArgs.linkConfig = settings[K.SETTING.SMART_LINKS_CONFIG];
    transforms.push(TRANSFORM_TYPE.PRETTY_PRINT);

    return transformContent(facet, text, transforms, transformArgs);
  };

  // if we fetched full data for a log, check why and handle appropriately
  const prevLogLineData = usePrevious(logLineData);
  useConditionalEffect(() => {
    const logEntryEdge = logLineData.getLogEntry.logEntry;
    if (logEntryEdge.continuationToken !== logToFetch?.continuationToken) {
      return; // race condition that should be very rare, since these requests are fast
    }
    const origin = logToFetch?.origin;
    if (origin === FETCH_ORIGIN.VALUE) {
      // user clicked to see full value of a facet
      const field = logToFetch?.field || '';
      const fullValue = logLineData.getLogEntry.logEntry.logEntry.values[field];
      const fullHtml = <Markup content={transformValue(field, fullValue)} noWrap disableLineBreaks={false} />;
      const seeMore: SeeMoreState = {
        field,
        value: fullHtml,
      };
      setSeeMore(seeMore); // set Recoil state which will result in SeeMoreDialog opening
    } else if (origin === FETCH_ORIGIN.ATTRIBUTES) {
      // user clicked to see all the attributes of the event
      setSelectedToken(logEntryEdge.continuationToken);
      setLogToInspect(logEntryEdge.logEntry);
    }
    setLogToFetch(null);
  }, logLineData && logLineData !== prevLogLineData);
};
